<script setup lang="ts">
import type { Project } from '~/utils/project/Project'
import ComboConecteModal from '~/components/simulation/ComboConecteModal.vue'
import { useAnalytics } from '~/composables/analytics/useAnalytics'
import { FEATURE_FLAGS } from '~/constants'
import hotjar from '~/services-v2/hotjar/hotjar'
import SimulatorService from '~/services/simulator/Simulator'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancing from '~/store-v2/financings/useFinancing'
import { getErrorMessage } from '~/utils/errors'
import { convertAnualRateToMonthly } from '~/utils/simulation/calculations'
import { resolveDefaultDueDay } from '~/utils/simulation/dueDay'
import { shouldCloseComboConnectModal } from '~/utils/simulation/modals'
import {
  calculateDefaultInstallmentForIncompleteProject,
  createSimulationOpportunitiesPayload,
  extractOldSimulationDefaults,
  extractProjectDefaults,
  isProjectComplete,
  isProjectInactive,
} from '~/utils/simulation/projects'

const router = useRouter()
const route = useRoute<any>()
const { d } = useI18n()

const id = ref(route.params.id as string)
const { trackEvent } = useAnalytics({ projectId: id })
const {
  data: financing,
  error,
  isError,
  isLoading,
  refetch: refetchFinancing,
} = useFinancing(id)

const projectResponse = ref<Project | undefined>(undefined)
const loadingProject = ref(false)
const simulation = ref({})
const projectIsFinished = ref(false)
const showComboConnectModal = ref(false)
const comboConecteModalInitialTab = ref<'benefits' | 'inverters'>('benefits')
const cetValueFinished = ref('')
const lastSimulation = ref<any>({})

const defaultCommission = ref(0)
const defaultCommissionRaw = ref(0)
const defaultGracePeriod = ref(1)
const defaultDueDay = ref<number | null>(1)
const defaultFirstDueDate = ref<null | string>(null)
const defaultInstallment = ref(60)
const showCommissionMoney = ref(false)

const riskRioGrandeDoSul = 9

const { data: solfacilPlusPointsFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.SHOW_SOLFACIL_PLUS_POINTS, false)
const { data: showCommissionMoneyFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.SHOW_COMMISSION_MONEY, false)
const { data: enableDynamicDueDayData, isFetched: isDynamicDueDayFetched } = useFeatureFlag<boolean>(
  FEATURE_FLAGS.ENABLE_DYNAMIC_DUE_DAY,
  false,
  true,
)
const enableDynamicDueDay = computed(() => enableDynamicDueDayData.value === true)

const errorMessage = computed(() => getErrorMessage(error.value))
const loading = computed(() => loadingProject.value || isLoading.value)
const mappingErrors = computed(() => ['NOT_FOUND', 'UNAUTHORIZED', 'UNKNOWN'].includes(errorMessage.value))

const { setProject } = useResumeStore()
const { setAmpera } = useAddonStore()
const { setSimulationOpportunities } = useResumeStore()

const simulatorService = new SimulatorService()

function openComboConnectModal(tab: 'benefits' | 'inverters' = 'benefits') {
  comboConecteModalInitialTab.value = tab
  showComboConnectModal.value = true
}

async function mountNewQuotas(project: Project): Promise<void> {
  try {
    const isEditMode = 'edit' in route.query
    const defaults = extractProjectDefaults(project, isEditMode, enableDynamicDueDay.value)

    defaultCommission.value = defaults.commission
    defaultCommissionRaw.value = defaults.commissionRaw
    defaultGracePeriod.value = defaults.gracePeriod
    defaultDueDay.value = defaults.dueDay
    defaultFirstDueDate.value = defaults.firstDueDate ? d(defaults.firstDueDate) : null
    defaultInstallment.value = defaults.installment
    projectIsFinished.value = defaults.isFinished
    simulation.value = project
    cetValueFinished.value = defaults.cet

    const addonAmpera = project.addons.filter(item => item.product_slug === 'Ampera')
    if (addonAmpera.length) {
      setAmpera(addonAmpera, addonAmpera[0].applied, project.can_select_ampera)
    }
  }
  catch (error) {
    console.error('Error mounting new quotas:', error)
    throw error
  }
}

async function mountOldQuotas(project?: Project, lastSimulation?: any): Promise<void> {
  if (!project || !lastSimulation.selected_installment) {
    return
  }

  const defaults = extractOldSimulationDefaults(project, lastSimulation.value, enableDynamicDueDay.value)

  if (!defaults) return

  defaultCommission.value = defaults.commission
  defaultCommissionRaw.value = defaults.commissionRaw
  defaultGracePeriod.value = defaults.gracePeriod
  defaultDueDay.value = defaults.dueDay
  defaultFirstDueDate.value = defaults.firstDueDate ? d(defaults.firstDueDate) : null
  defaultInstallment.value = defaults.installment
  projectIsFinished.value = defaults.isFinished

  const parsedSimulation = {
    ...lastSimulation,
    selected_installments: lastSimulation.selected_installment,
    taxa_de_juros: lastSimulation.selected_installment?.taxa_de_juros
      ? convertAnualRateToMonthly(lastSimulation.selected_installment.taxa_de_juros)
      : lastSimulation.taxa_de_juros,
  }
  simulation.value = parsedSimulation
}

async function setOpportunities(project: Project): Promise<void> {
  if (!solfacilPlusPointsFeatureFlag.value) return

  try {
    const payload = createSimulationOpportunitiesPayload(project)
    const response = await simulatorService.post_simulation_opportunities(payload)

    if (response.data) {
      setSimulationOpportunities(response.data)
    }
  }
  catch (error) {
    console.error('Error setting opportunities:', error)
  }
}

function applyDueDayDefault(project: Project): void {
  const isEditMode = 'edit' in route.query
  if (isProjectComplete(project)) {
    defaultDueDay.value = extractProjectDefaults(project, isEditMode, enableDynamicDueDay.value).dueDay
    return
  }

  defaultDueDay.value = resolveDefaultDueDay(
    project.due_day ?? lastSimulation.value?.due_day,
    { isComplete: false, useDynamicDefault: enableDynamicDueDay.value },
  )
}

async function loadProjectData(): Promise<void> {
  try {
    loadingProject.value = true

    const { data: project } = await simulatorService.get_project(route.params.id)

    if (project && isProjectInactive(project)) {
      router.push('/projects')
      return
    }

    if (project && !isProjectComplete(project)) {
      defaultInstallment.value = calculateDefaultInstallmentForIncompleteProject(project)
    }

    if (project) {
      projectResponse.value = project
      await setOpportunities(project)
      setProject(project)
    }

    if (project && isProjectComplete(project)) {
      await mountNewQuotas(project)
      applyDueDayDefault(project)
      return
    }

    const { data } = await simulatorService.get_last_simulation_by_project(route.params.id)
    lastSimulation.value = data

    if (lastSimulation.value && lastSimulation.value.installments) {
      mountOldQuotas(project, lastSimulation)
    }

    if (project)
      applyDueDayDefault(project)
  }
  catch (error) {
    console.error('Error loading project data:', error)
  }
  finally {
    loadingProject.value = false
  }
}

onBeforeMount(async () => {
  await refetchFinancing()

  if (financing.value?.step === 'simulation' && financing.value?.status === 'reproved') {
    return router.push(`/simulation/result/${financing.value?.project_id}`)
  }
})

onMounted(async () => {
  await loadProjectData()
})

const hasTrackedDetailsView = ref(false)
watch(isDynamicDueDayFetched, (fetched) => {
  if (!fetched || hasTrackedDetailsView.value)
    return

  hasTrackedDetailsView.value = true
  const flagValue = enableDynamicDueDay.value
  trackEvent('financing_details_viewed', { enable_dynamic_due_day: flagValue })
  hotjar.setUserProperties({ enable_dynamic_due_day: flagValue })
}, { immediate: true })

watch([enableDynamicDueDay, projectResponse], () => {
  if (projectResponse.value)
    applyDueDayDefault(projectResponse.value)
})

watch(showCommissionMoneyFeatureFlag, (value) => {
  showCommissionMoney.value = value || false
}, { immediate: true })

watch([projectResponse, loadingProject], ([project, isLoading]) => {
  if (shouldCloseComboConnectModal(project, isLoading, showComboConnectModal.value)) {
    showComboConnectModal.value = false
  }
})
</script>

<template>
  <div class="container">
    <NavigationHeaderBar
      stage="simulation"
      :title="projectResponse?.project_name"
      :financing="financing"
    />
    <div v-if="loading" class="installments-container-loader">
      <div class="loader-project z-[19px]" />
      <div class="loader-project hidden md:system:block z-[19px]" />
    </div>
    <template v-else>
      <template v-if="isError && mappingErrors">
        <SharedErrorsCheckAccess :error-message="errorMessage" />
      </template>
      <template v-else>
        <SolAlert
          v-if="projectResponse?.decision_pre_analisys === riskRioGrandeDoSul && !loadingProject"
          id="title"
          class="mb-4xs md:site:mb-10"
          :title="$t('simulation.info_risk.RS_title')"
          feedback="warning"
        >
          <div>
            <p>{{ $t('simulation.info_risk.RS_description_conditions') }}</p>
            <ul class="list-disc list-inside">
              <li> {{ $t('simulation.info_risk.RS_description_grace') }} </li>
              <li> {{ $t('simulation.info_risk.RS_description_eligible_area') }} </li>
            </ul>
          </div>
        </SolAlert>
        <SimulationDetails
          v-if="projectResponse"
          :simulation="simulation"
          :disable-fields="!projectIsFinished"
          :project="projectResponse"
          :default-installment="defaultInstallment"
          :default-grace-period="defaultGracePeriod"
          :default-due-day="defaultDueDay"
          :default-first-due-date="defaultFirstDueDate"
          :default-commission="defaultCommission"
          :default-commission-raw="defaultCommissionRaw"
          :cet="projectIsFinished ? cetValueFinished : lastSimulation?.selected_installment?.cet"
          :show-commission-money="showCommissionMoney"
          @open-combo-connect-modal="openComboConnectModal"
        />
        <div v-else class="my-4 card-container flex flex-col justify-between w-full align-middle py-lg text-center items-center">
          <img src="/images/not-found.png" class="not-found">
          <h1 class="fonts-heading-h1">
            {{ $t('noResults.title') }}
          </h1>
          <p class="fonts-subtitle-medium">
            {{ $t('noResults.description') }}
          </p>
        </div>
      </template>
    </template>
  </div>

  <ComboConecteModal
    :is-open="showComboConnectModal && !loading"
    :project="projectResponse || null"
    :initial-tab="comboConecteModalInitialTab"
    @update:is-open="showComboConnectModal = $event"
  />
</template>

<style lang="scss">
.container {
  @apply flex flex-col items-start px-micro pb-5 mt-6 gap-3;

  @screen md:system {
    @apply px-2xs;
  }
}

.installments-container-loader{
  @apply w-full block;
  @screen md:system {
    @apply grid gap-4;
    grid-template-columns: minmax(0, 1fr) 300px
  }
}

.loader-project {
  @apply h-96 rounded-lg mt-5;
  @apply top-0 right-0 bottom-0 left-0;
  background: rgba(#fff, 0.9)
    url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat
    center center;
}

.content-alert {
  @apply text-brand-primary-dark;
}

.card-container{
  @apply px-12 my-6 bg-neutral-high-pure rounded-lg;

}

.list-modal {

  @apply list-disc list-inside leading-4 text-3xs ml-1;
}

.title-benefits,
.list-modal,
.content-alert {
  font-family: "Lato",sans-serif;
}

.combo-connect-modal {
  z-index: 99999 !important;
}
</style>
